import React, { useId, useState } from 'react';

// A disclosure whose header stays useful while collapsed: the title, a count and
// any badges all read without opening it. On a large roster the headers are what
// you scan and the bodies are what you drill into, so collapsing has to lose the
// detail without losing the shape.
//
// `tone` sets the visual weight. Three of these nest — company, then the admins
// group inside it, then a manager's subsection — so each level is quieter than
// its parent, and depth stays legible from the header alone rather than from
// indentation the eye has to measure.
const TONES = {
  company: {
    header: 'px-5 py-4 bg-gray-900/60',
    title: 'text-xl font-bold text-white',
    chevron: 'w-5 h-5',
  },
  group: {
    header: 'px-6 py-3 bg-gray-900/40',
    title: 'text-sm font-semibold text-white',
    chevron: 'w-4 h-4',
  },
  sub: {
    header: 'px-6 py-2.5 bg-gray-900/25',
    title: 'text-sm font-medium text-gray-200',
    chevron: 'w-4 h-4',
  },
};

const Chevron = ({ open, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} flex-shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
      clipRule="evenodd"
    />
  </svg>
);

const CollapsibleSection = ({
  tone = 'group',
  title,
  count,
  countLabel,
  badges,
  right,
  defaultOpen = true,
  className = '',
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const styles = TONES[tone] || TONES.group;

  return (
    <div className={className}>
      <div className={`${styles.header} flex items-center justify-between gap-3`}>
        {/* The actions sit beside the toggle, never inside it: a button nested
            in a button is invalid, and a stray click on Remove must not also
            collapse the section it was aimed at. */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={bodyId}
          // select-none because these headers get clicked over and over while
          // opening and closing groups, and a drag of a pixel would otherwise
          // leave the title highlighted. hover:bg-transparent because App.css
          // turns every hovered button blue, and its :hover outranks a lone
          // bg-transparent.
          className="flex items-center gap-2 min-w-0 flex-1 text-left bg-transparent hover:bg-transparent p-0 select-none hover:opacity-90 transition-opacity"
        >
          <Chevron open={open} className={styles.chevron} />
          <span className={`${styles.title} truncate`}>{title}</span>
          {count != null && (
            <span className="text-xs text-gray-500 flex-shrink-0">
              {count}
              {countLabel ? ` ${countLabel}` : ''}
            </span>
          )}
          {badges}
        </button>
        {right && <div className="flex items-center gap-2 flex-shrink-0">{right}</div>}
      </div>
      <div id={bodyId} hidden={!open}>
        {children}
      </div>
    </div>
  );
};

export default CollapsibleSection;
